import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Linkedin, MessageCircle, ChevronDown, FileSpreadsheet, BarChart3, FolderOpen } from "lucide-react";
import { API, WHATSAPP_NUMBER, CONTACT_EMAIL, LOCATIONS, SOCIAL_LINKS } from "@/lib/constants";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/servizi", label: "Servizi" },
  { path: "/controllo-di-gestione-micro-piccole-imprese", label: "Controllo di Gestione" },
  { path: "/pannello-direzionale", label: "Pannello Direzionale" },
  { path: "/forfettari", label: "Forfettari" },
  { path: "/libro", label: "Il Libro", highlight: true },
  { path: "/approfondimenti", label: "Approfondimenti" },
  { path: "/chi-siamo", label: "Chi Siamo" },
  { path: "/strumenti", label: "Strumenti", hasDropdown: true },
  { path: "/contatti", label: "Contatti" }
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [strumentiOpen, setStrumentiOpen] = useState(false);
  const [strumentiMobileOpen, setStrumentiMobileOpen] = useState(false);
  const [tools, setTools] = useState([]);
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setStrumentiOpen(false);
    setStrumentiMobileOpen(false);
  }, [location]);

  // Fetch tools once
  useEffect(() => {
    axios.get(`${API}/products`)
      .then(r => setTools((Array.isArray(r.data) ? r.data : []).filter(t => !t.hide_from_catalog)))
      .catch(() => setTools([]));
  }, []);

  const openDropdown = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setStrumentiOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setStrumentiOpen(false), 180);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 glass" data-testid="header">
      <div className="max-w-[1720px] mx-auto px-6 2xl:px-10">
        <div className="flex items-center justify-between gap-4 h-20">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="logo-link">
            <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-gold to-yellow-600 rounded flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <span className="font-heading text-black text-2xl font-bold">SC</span>
            </div>
            <div className="hidden sm:block min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-heading text-base min-[1400px]:text-lg 2xl:text-xl text-[#0D1527] tracking-wide whitespace-nowrap truncate">SC DOTTORI COMMERCIALISTI</p>
                <span className="bg-red-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-sm shadow-lg transform rotate-[-8deg] translate-y-[-2px] inline-block flex-shrink-0 whitespace-nowrap" style={{boxShadow: '2px 2px 8px rgba(0,0,0,0.25)'}}>e molto di piu!</span>
              </div>
              <p className="text-[10px] min-[1400px]:text-xs text-gold tracking-wide min-[1400px]:tracking-widest whitespace-nowrap truncate">IL COMMERCIALISTA COME PARTNER STRATEGICO</p>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-2 2xl:gap-4 flex-shrink-0">
            {NAV_LINKS.map(link => {
              if (link.hasDropdown) {
                return (
                  <div
                    key={link.path}
                    className="relative"
                    onMouseEnter={openDropdown}
                    onMouseLeave={scheduleClose}
                    ref={dropdownRef}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 text-[11px] font-medium tracking-wide animated-underline transition-colors whitespace-nowrap ${
                        ['/strumenti','/portale-documenti'].some(p => location.pathname.startsWith(p)) ? "text-gold" : "text-[#0D1527]/70 hover:text-[#0D1527]"
                      }`}
                      data-testid="nav-strumenti"
                    >
                      {link.label.toUpperCase()}
                      <ChevronDown size={12} className={`transition-transform ${strumentiOpen ? 'rotate-180' : ''}`} />
                    </Link>
                    {strumentiOpen && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-80"
                        data-testid="strumenti-dropdown"
                      >
                        <div className="bg-white/95 backdrop-blur-md border border-[#E8E2D5] rounded-lg shadow-2xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-[#EFE9DE]">
                            <p className="text-gold text-[10px] tracking-[0.25em] font-bold">STRUMENTI DIGITALI</p>
                          </div>
                          <div className="py-1 max-h-[60vh] overflow-y-auto">
                                                        <Link
                              to="/portale-documenti"
                              className="flex items-start gap-3 px-4 py-3 hover:bg-[#F4EFEA] transition-colors group border-b border-[#EFE9DE]"
                              data-testid="strumenti-dropdown-item-portale-documenti"
                            >
                              <div className="w-9 h-9 rounded bg-[#F7F0E1] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                                <FolderOpen size={16} className="text-gold" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[#0D1527] text-sm font-semibold truncate">Portale Documenti</p>
                                <p className="text-[#8B94A6] text-xs truncate">Area riservata ai clienti dello studio</p>
                              </div>
                            </Link>
                            {/* Check-up Fiscale — pagina statica: <a> non <Link> */}
                            <a
                              href="/check-up-fiscale/"
                              className="flex items-start gap-3 px-4 py-3 hover:bg-[#F4EFEA] transition-colors group border-b border-[#EFE9DE]"
                              data-testid="strumenti-dropdown-item-check-up-fiscale"
                            >
                              <div className="w-9 h-9 rounded bg-[#F7F0E1] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                                <FileSpreadsheet size={16} className="text-gold" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[#0D1527] text-sm font-semibold truncate">Check-up Fiscale</p>
                                <p className="text-gold text-xs truncate">Gratuito · 3 minuti</p>
                              </div>
                            </a>
                            {/* Valutazione CPB — pagina statica: <a> non <Link> */}
                            <a
                              href="/strumenti/valutazione-concordato-preventivo-biennale/"
                              className="flex items-start gap-3 px-4 py-3 hover:bg-[#F4EFEA] transition-colors group border-b border-[#EFE9DE]"
                              data-testid="strumenti-dropdown-item-valutazione-cpb"
                            >
                              <div className="w-9 h-9 rounded bg-[#F7F0E1] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                                <FileSpreadsheet size={16} className="text-gold" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[#0D1527] text-sm font-semibold truncate">Valutazione Concordato Preventivo Biennale</p>
                                <p className="text-[#8B94A6] text-xs truncate">Report di convenienza · da 47 €</p>
                              </div>
                            </a>
                            {tools.length === 0 && (
                              <div className="px-4 py-3 text-[#8B94A6] text-xs">Caricamento...</div>
                            )}
                            {tools.map(t => (
                              <Link
                                key={t.id || t.slug}
                                to={`/strumenti/${t.slug}`}
                                className="flex items-start gap-3 px-4 py-3 hover:bg-[#F4EFEA] transition-colors group"
                                data-testid={`strumenti-dropdown-item-${t.slug}`}
                              >
                                <div className="w-9 h-9 rounded bg-[#F7F0E1] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                                  <FileSpreadsheet size={16} className="text-gold" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[#0D1527] text-sm font-semibold truncate">{t.title}</p>
                                  <p className="text-[#8B94A6] text-xs truncate">{t.short_description || ''}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                          <Link to="/strumenti" className="block px-4 py-3 text-center text-gold text-xs font-bold tracking-wider hover:bg-[#F7F0E1] border-t border-[#EFE9DE] transition-colors" data-testid="strumenti-dropdown-catalogo">
                            VEDI TUTTI GLI STRUMENTI →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[11px] font-medium tracking-wide animated-underline transition-colors whitespace-nowrap ${
                    location.pathname === link.path ? "text-gold" : link.highlight ? "text-gold hover:text-[#96742F]" : "text-[#0D1527]/70 hover:text-[#0D1527]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label.toUpperCase()}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 text-[#0D1527] hover:text-gold transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="xl:hidden glass animate-fade-in max-h-[calc(100vh-90px)] overflow-y-auto overscroll-contain">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
            {NAV_LINKS.map(link => {
              if (link.hasDropdown) {
                return (
                  <div key={link.path}>
                    <div
                      className={`flex items-stretch rounded overflow-hidden ${
                        ['/strumenti','/portale-documenti'].some(p => location.pathname.startsWith(p)) ? "bg-gold text-white" : "text-[#0D1527]"
                      }`}
                    >
                      <Link
                        to={link.path}
                        className="flex-1 px-4 py-4 font-medium tracking-wide hover:bg-[#F4EFEA] transition-all"
                        data-testid="mobile-strumenti-link"
                      >
                        {link.label.toUpperCase()}
                      </Link>
                      <button
                        onClick={() => setStrumentiMobileOpen(!strumentiMobileOpen)}
                        aria-label={strumentiMobileOpen ? "Chiudi elenco strumenti" : "Apri elenco strumenti"}
                        className="px-4 flex items-center hover:bg-[#F4EFEA] transition-all"
                        data-testid="mobile-strumenti-toggle"
                      >
                        <ChevronDown size={18} className={`transition-transform ${strumentiMobileOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {strumentiMobileOpen && (
                      <div className="ml-2 mt-1 border-l border-gold/30 pl-3 space-y-1" data-testid="mobile-strumenti-dropdown">
                        <Link to="/strumenti" className="block px-3 py-2 text-sm text-gold hover:bg-[#F4EFEA] rounded" data-testid="mobile-strumenti-catalogo">
                          Prodotti e strumenti digitali
                        </Link>
                                                <Link to="/portale-documenti" className="block px-3 py-2 text-sm text-[#3B4658] hover:bg-[#F4EFEA] rounded" data-testid="mobile-strumenti-portale-documenti">
                          Portale Documenti
                        </Link>
                        {/* Check-up Fiscale — pagina statica: <a> non <Link> */}
                        <a href="/check-up-fiscale/" className="block px-3 py-2 text-sm text-[#3B4658] hover:bg-[#F4EFEA] rounded">
                          Check-up Fiscale <span className="text-gold text-xs">(gratuito)</span>
                        </a>
                        {/* Valutazione CPB — pagina statica: <a> non <Link> */}
                        <a href="/strumenti/valutazione-concordato-preventivo-biennale/" className="block px-3 py-2 text-sm text-[#3B4658] hover:bg-[#F4EFEA] rounded">
                          Valutazione Concordato Preventivo Biennale
                        </a>
                        {tools.map(t => (
                          <Link
                            key={t.id || t.slug}
                            to={`/strumenti/${t.slug}`}
                            className="block px-3 py-2 text-sm text-[#3B4658] hover:bg-[#F4EFEA] rounded"
                          >
                            {t.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-4 rounded font-medium tracking-wide transition-all ${
                    location.pathname === link.path ? "bg-gold text-white" : "text-[#0D1527] hover:bg-[#F4EFEA]"
                  }`}
                >
                  {link.label.toUpperCase()}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#101B2B] border-t border-gold/25" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-gold to-yellow-600 rounded flex items-center justify-center">
              <span className="font-heading text-black text-2xl font-bold">SC</span>
            </div>
            <div>
              <p className="font-heading text-lg text-white">SC DOTTORI COMMERCIALISTI</p>
              <p className="text-xs text-gold/80 tracking-wider">IL COMMERCIALISTA COME PARTNER STRATEGICO</p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-sm shadow-lg transform rotate-[-6deg]" style={{boxShadow: '2px 3px 10px rgba(0,0,0,0.5)'}}>e molto di piu!</span>
          </div>
          <p className="text-sm text-white/60 leading-relaxed mb-6">
            20 anni di esperienza. Sede a Ravenna, clienti in tutta Italia.
          </p>
          <div className="flex gap-4">
            {[
              { href: SOCIAL_LINKS.facebook, icon: Facebook },
              { href: SOCIAL_LINKS.instagram, icon: Instagram },
              { href: SOCIAL_LINKS.linkedin, icon: Linkedin }
            ].map((social, i) => (
              <a key={i} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-gold hover:text-black rounded-full flex items-center justify-center transition-all">
                <social.icon size={18} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-heading text-xl text-white mb-6 tracking-wide">NAVIGAZIONE</h4>
          <nav className="flex flex-col gap-3">
            {NAV_LINKS.map(l => (
              <Link key={l.path} to={l.path} className="text-sm text-white/60 hover:text-gold transition-colors">{l.label}</Link>
            ))}
          </nav>
          <h4 className="font-heading text-sm text-white/80 mt-8 mb-4 tracking-[0.2em]">STRUMENTI DIGITALI</h4>
          <nav className="flex flex-col gap-3">
            <Link to="/portale-documenti" className="text-sm text-white/60 hover:text-gold transition-colors">Portale Documenti</Link>
            {/* Pagine statiche: <a> non <Link> */}
            <a href="/check-up-fiscale/" className="text-sm text-white/60 hover:text-gold transition-colors">Check-up Fiscale</a>
            <a href="/strumenti/valutazione-concordato-preventivo-biennale/" className="text-sm text-white/60 hover:text-gold transition-colors">Valutazione Concordato Preventivo Biennale</a>
          </nav>
        </div>

        <div>
          <h4 className="font-heading text-xl text-white mb-6 tracking-wide">CONTATTI</h4>
          <div className="flex flex-col gap-4">
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-white/60 hover:text-gold transition-colors">
              <Phone size={18} />
              +39 329 013 4945
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-sm text-white/60 hover:text-gold transition-colors">
              <Mail size={18} />
              {CONTACT_EMAIL}
            </a>
            <div className="flex items-start gap-3 text-sm text-white/60">
              <MapPin size={18} className="flex-shrink-0 mt-0.5" />
              <span>{LOCATIONS.main.address}, {LOCATIONS.main.city}</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-xl text-white mb-6 tracking-wide">SEDI</h4>
          <p className="text-sm text-white/60 mb-4">Sede legale e operativa: Ravenna</p>
          <p className="text-sm text-white/60 mb-4">Sala riunioni: {LOCATIONS.meeting.address}, Ravenna</p>
          <p className="text-sm text-white/60">Anche a: {LOCATIONS.others.join(", ")}</p>
        </div>
      </div>

      <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="text-xs text-white/40 text-center md:text-left leading-relaxed">
          <p>SC Dottori Commercialisti SRL STP · P.IVA IT02677200392</p>
          <p className="mt-1">Dott.ssa Silvia Casali · Dottore Commercialista, iscritta all'Ordine dei Dottori Commercialisti e degli Esperti Contabili di Ravenna al n. 511 A</p>
        </div>
        <div className="flex gap-6">
          <Link to="/privacy-policy" className="text-xs text-white/40 hover:text-white/60 transition-colors">Privacy Policy</Link>
          <a href="/intelligenza-artificiale/" className="text-xs text-white/40 hover:text-white/60 transition-colors">Utilizzo dell'intelligenza artificiale</a>
          <Link to="/cookie-policy" className="text-xs text-white/40 hover:text-white/60 transition-colors">Cookie Policy</Link>
          <button
            type="button"
            onClick={() => { if (window.SCCookieBanner) window.SCCookieBanner.open(); }}
            className="text-xs text-white/40 hover:text-white/60 transition-colors bg-transparent border-0 p-0 cursor-pointer"
            data-testid="footer-manage-cookies"
          >
            Gestisci cookie
          </button>
        </div>
      </div>
    </div>
  </footer>
);

export const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Buongiorno, vorrei informazioni sui vostri servizi.`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-button group"
    data-testid="whatsapp-floating"
  >
    <MessageCircle size={32} className="group-hover:scale-110 transition-transform" />
  </a>
);
